import { forwardRef, useImperativeHandle, useRef, type ClipboardEvent, type DragEvent } from "react"
import { TextArea, Text } from "@radix-ui/themes"
import { getImageFilesFromClipboard } from "@/lib/clipboard"

export interface MarkdownEditorInputHandle {
  insertMarkdownImages: (markdownImages: string, insertPosition?: number) => void
}

interface MarkdownEditorInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  uploadStatus: "idle" | "uploading" | "success" | "error"
  onUploadImages: (files: File[], insertPosition?: number) => void
  onCancelUpload: () => void
}

export const MarkdownEditorInput = forwardRef<MarkdownEditorInputHandle, MarkdownEditorInputProps>(
  function MarkdownEditorInput(
    {
      value,
      onChange,
      placeholder = "Enter your markdown here...",
      uploadStatus,
      onUploadImages,
      onCancelUpload,
    },
    ref,
  ) {
    const textAreaRef = useRef<HTMLTextAreaElement>(null)

    useImperativeHandle(ref, () => ({
      insertMarkdownImages(markdownImages, insertPosition) {
        const textArea = textAreaRef.current
        if (!textArea) return

        const position = insertPosition ?? textArea.value.length
        const before = textArea.value.substring(0, position).replace(/[ \t\r\n]*$/, "")
        const after = textArea.value.substring(position).replace(/^[ \t\r\n]*/, "")
        const replacement = `${before.length > 0 ? "\n\n" : ""}${markdownImages}${after.length > 0 ? "\n\n" : ""}`
        const start = before.length
        const end = textArea.value.length - after.length

        textArea.focus()
        textArea.setSelectionRange(start, end)

        if (!document.execCommand("insertText", false, replacement)) {
          textArea.setRangeText(replacement, start, end, "end")
          textArea.dispatchEvent(new Event("input", { bubbles: true }))
        }
      },
    }))

    const getCaretPoint = (textArea: HTMLTextAreaElement, position: number): DOMRect => {
      const style = window.getComputedStyle(textArea)
      const mirror = document.createElement("div")
      const marker = document.createElement("span")
      const properties = [
        "borderBottomWidth",
        "borderLeftWidth",
        "borderRightWidth",
        "borderTopWidth",
        "boxSizing",
        "fontFamily",
        "fontFeatureSettings",
        "fontKerning",
        "fontSize",
        "fontStretch",
        "fontStyle",
        "fontVariant",
        "fontWeight",
        "letterSpacing",
        "lineHeight",
        "paddingBottom",
        "paddingLeft",
        "paddingRight",
        "paddingTop",
        "tabSize",
        "textAlign",
        "textIndent",
        "textTransform",
        "wordSpacing",
      ] as const

      properties.forEach((property) => {
        mirror.style[property] = style[property]
      })

      const rect = textArea.getBoundingClientRect()
      mirror.style.position = "fixed"
      mirror.style.left = `${rect.left}px`
      mirror.style.top = `${rect.top}px`
      mirror.style.width = `${rect.width}px`
      mirror.style.height = `${rect.height}px`
      mirror.style.overflow = "auto"
      mirror.style.pointerEvents = "none"
      mirror.style.visibility = "hidden"
      mirror.style.whiteSpace = "pre-wrap"
      mirror.style.overflowWrap = "break-word"

      marker.textContent = "\u200b"
      mirror.append(document.createTextNode(textArea.value.slice(0, position)))
      mirror.append(marker)
      mirror.append(document.createTextNode(textArea.value.slice(position)))
      document.body.append(mirror)
      mirror.scrollTop = textArea.scrollTop
      mirror.scrollLeft = textArea.scrollLeft

      const markerRect = marker.getBoundingClientRect()
      mirror.remove()

      return markerRect
    }

    const getDropPosition = (ev: DragEvent<HTMLTextAreaElement>): number => {
      const textArea = textAreaRef.current
      if (!textArea) return 0

      const isBeforeDropPoint = (caret: DOMRect) => {
        if (ev.clientY < caret.top) return false
        if (ev.clientY > caret.bottom) return true

        return caret.left < ev.clientX
      }

      let low = 0
      let high = textArea.value.length

      while (low < high) {
        const mid = Math.floor((low + high) / 2)
        const caret = getCaretPoint(textArea, mid)

        if (isBeforeDropPoint(caret)) {
          low = mid + 1
        } else {
          high = mid
        }
      }

      const previousPosition = Math.max(0, low - 1)
      const previousCaret = getCaretPoint(textArea, previousPosition)
      const currentCaret = getCaretPoint(textArea, low)
      const previousDistance = Math.hypot(
        previousCaret.left - ev.clientX,
        previousCaret.top + previousCaret.height / 2 - ev.clientY,
      )
      const currentDistance = Math.hypot(
        currentCaret.left - ev.clientX,
        currentCaret.top + currentCaret.height / 2 - ev.clientY,
      )

      return previousDistance < currentDistance ? previousPosition : low
    }

    const handlePaste = (ev: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = getImageFilesFromClipboard(ev)
      if (files.length > 0) {
        ev.preventDefault()
        const cursorPosition = textAreaRef.current?.selectionStart ?? value.length
        onUploadImages(files, cursorPosition)
      }
    }

    const handleDragOver = (ev: DragEvent<HTMLTextAreaElement>) => {
      ev.preventDefault()
      ev.dataTransfer.dropEffect = "copy"
    }

    const handleDrop = (ev: DragEvent<HTMLTextAreaElement>) => {
      ev.preventDefault()

      const files = Array.from(ev.dataTransfer.files).filter((file) =>
        file.type.startsWith("image/"),
      )

      if (files.length > 0) {
        const dropPosition = getDropPosition(ev)
        onUploadImages(files, dropPosition)
      }
    }

    return (
      <div className="relative flex h-full flex-col">
        {uploadStatus === "uploading" && (
          <div
            className="absolute inset-0 z-10 flex cursor-pointer items-center justify-center bg-white/80"
            onClick={onCancelUpload}
          >
            <div className="flex items-center gap-2 rounded border border-gray-300 bg-white px-6 py-3 text-gray-700">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              Uploading images...
            </div>
          </div>
        )}
        <TextArea
          ref={textAreaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={undefined}
          onPaste={handlePaste}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="h-full resize-none !font-mono"
        />
        <div className="mt-2">
          <Text color="gray" size="1">
            Tip: You can paste or drag & drop images to automatically upload and insert them
          </Text>
        </div>
      </div>
    )
  },
)
